import { useNavigate } from 'react-router-dom';

import { zonesTableTabsTableColumns } from 'configs';
import { ROUTES } from 'consts';
import { EZoneStatus } from 'enums';
import { IZone, IZoneWithActions } from 'models';
import { useFetchZoneQuery, useFetchZonesQuery } from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';

type BuildingId = number | null;

type UseZonesProps = {
  buildingId: BuildingId;
};

export const useZones = ({ buildingId }: UseZonesProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const { data = [], ...rest } = useFetchZonesQuery({ orgSlug });

  /**
   * TODO: implement safe zone type again
   */
  let zones = data.map((zone: IZone) => ({
    ...zone,
    countdown: zone.countdown_disabled ? -1 : zone.cleaning_interval_seconds || 0,
    floorInfo: zone?.floor?.floor || `${zone.building_floor_id}`,
    rating: zone.rating || 0,
    lastServicedBy: (zone.logs || []).length > 0 ? zone.logs[0]?.day_porter?.name || '' : '',
    serviceItemsText: (zone?.service_items || []).map((serviceItem) => serviceItem.name).join(', '),
  }));

  if (buildingId) {
    zones = zones.filter((zone: IZone) => zone.building_id === buildingId);
  }

  const activeZones = zones.filter((zone: any) => zone.status === EZoneStatus.ACTIVE);
  const pendingZones = zones.filter((zone: any) => zone.status === EZoneStatus.PENDING);
  const inactiveZones = zones.filter((zone: any) => zone.status === EZoneStatus.INACTIVE);

  return {
    data: {
      all: zones as IZone[],
      active: activeZones as IZone[],
      pending: pendingZones as IZone[],
      inactive: inactiveZones as IZone[],
    },
    ...rest,
  };
};

type Schedule = {
  id: number;
  clean_code_zone_id: number;
  time: string;
  created_at: string;
  updated_at: string;
};

type Building = {
  address: string;
};

export type Zone = {
  id: number | null;
  schedule: Schedule[];
  code: string;
  name: string;
  next_cleaning_time?: string;
  building_id: number;
  building: Building;
};

type UseZoneProps = {
  zoneId: number;
};

export const useZone = ({ zoneId }: UseZoneProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const { data: zone = {}, ...rest } = useFetchZoneQuery({ orgSlug, id: zoneId });

  const data: IZone = { ...zone };

  return { data, ...rest };
};

type UseZonesCountProps = UseZonesProps;

export const useZonesCount = ({ buildingId }: UseZonesCountProps) => {
  const {
    data: { all: allZones, active: activeZones, pending: pendingZones, inactive: inactiveZones },
    ...rest
  } = useZones({ buildingId });

  const all = allZones.length;
  const active = activeZones.length;
  const pending = pendingZones.length;
  const inactive = inactiveZones.length;

  return { data: { all, active, pending, inactive }, ...rest };
};

type UseZonesTableRowsProps = UseZonesProps & { setState?: (state: any) => void };

export const useZonesTableRows = ({ buildingId, setState = () => {} }: UseZonesTableRowsProps) => {
  const navigate = useNavigate();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const {
    data: { all: zones },
    ...rest
  } = useZones({ buildingId });

  const viewCountdown = (zone: IZone) => window.open(`${process.env.REACT_APP_CLEANCODE_URL}/${zone.code}`);
  const manageServiceItem = (zone: IZone) =>
    setState((prev: any) => ({
      ...prev,
      zoneId: zone.id,
      categoryId: zone.clean_code_zone_category_id,
      visibleZoneServiceItemsDialog: true,
    }));
  const viewCleaningHistory = (zone: IZone) =>
    navigate(`/${orgSlug}/${ROUTES.ZONE_CLEANING_HISTORY.replace(':zoneId', `${zone.id}`)}`);
  const copyZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneCopyDialog: true }));
  const editZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneUpdateDialog: true }));
  const deactivateZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneDeactivateDialog: true }));
  const purchaseZone = () => navigate(`/${orgSlug}/${ROUTES.CART}`);
  const deleteZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneDeleteDialog: true }));
  const activateZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneActivateDialog: true }));

  const rows = zones.map(
    (zone: IZone) =>
      ({
        ...zone,
        isLoading: false,
        viewCountdown,
        manageServiceItem,
        viewCleaningHistory,
        copyZone,
        editZone,
        deactivateZone,
        purchaseZone,
        deleteZone,
        activateZone,
      } as IZoneWithActions),
  );

  return { data: rows, ...rest };
};

type UseActiveZonesTableRowsProps = UseZonesTableRowsProps;

export const useActiveZonesTableRows = ({ buildingId, setState }: UseActiveZonesTableRowsProps) => {
  const { data: allRows, ...rest } = useZonesTableRows({ buildingId, setState });

  const columnKeys = [
    'product',
    'countdown',
    'code',
    'name',
    'floorInfo',
    'rating',
    'serviceItemsText',
    'lastServicedBy',
    'actions',
  ];

  const columns = zonesTableTabsTableColumns.filter((column) => columnKeys.includes(column.id));

  const rows = allRows.filter((row: any) => row.status === EZoneStatus.ACTIVE);

  return {
    data: {
      columnKeys,
      columns,
      rows,
    },
    ...rest,
  };
};

type UsePendingZonesTableRowsProps = UseZonesTableRowsProps;

export const usePendingZonesTableRows = ({ buildingId, setState }: UsePendingZonesTableRowsProps) => {
  const { data: allRows, ...rest } = useZonesTableRows({ buildingId, setState });

  const columnKeys = [
    'product',
    'code',
    'name',
    'floorInfo',
    'rating',
    'serviceItemsText',
    'lastServicedBy',
    'actions',
  ];

  const columns = zonesTableTabsTableColumns.filter((column) => columnKeys.includes(column.id));

  const rows = allRows.filter((row: any) => row.status === EZoneStatus.PENDING);

  return {
    data: {
      columnKeys,
      columns,
      rows,
    },
    ...rest,
  };
};

type UseInactiveZonesTableRows = UseZonesTableRowsProps;

export const useInactiveZonesTableRows = ({ buildingId, setState }: UseInactiveZonesTableRows) => {
  const { data: allRows, ...rest } = useZonesTableRows({ buildingId, setState });

  const columnKeys = [
    'product',
    'code',
    'name',
    'floorInfo',
    'rating',
    'serviceItemsText',
    'lastServicedBy',
    'actions',
  ];

  const columns = zonesTableTabsTableColumns.filter((column) => columnKeys.includes(column.id));

  const rows = allRows.filter((row: any) => row.status === EZoneStatus.INACTIVE);

  return {
    data: {
      columnKeys,
      columns,
      rows,
    },
    ...rest,
  };
};
